import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';

const AdminFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeedbacks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/get_feedbacks.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setFeedbacks(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const toggleApproval = async (id, currentStatus) => {
    // Current status comes as string or int depending on PDO config, ensure strict comparison
    const newStatus = parseInt(currentStatus) === 1 ? 0 : 1;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/approve_feedback.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, is_approved: newStatus })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setFeedbacks(feedbacks.map(fb => fb.id === id ? { ...fb, is_approved: newStatus } : fb));
      } else {
        alert(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, idx) => (
      <span key={idx} className={idx < rating ? "text-amber-400" : "text-slate-200"}>★</span>
    ));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-32 bg-slate-50 rounded-2xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Student Feedback</h2>
        <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-bold border border-emerald-100">
          Total: {feedbacks.length}
        </div>
      </div>

      {feedbacks.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-100">
          <span className="text-4xl mb-4 block">📝</span>
          <p className="text-slate-500 font-medium">No feedback submitted yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {feedbacks.map((fb) => (
            <div key={fb.id} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-lg transition-shadow relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
              
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div>
                  <h3 className="font-black text-slate-800 text-lg">{fb.name}</h3>
                  {fb.registration_id && (
                    <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mt-0.5">ID/Subject: {fb.registration_id}</p>
                  )}
                </div>
                <div className="text-lg tracking-widest bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 shadow-inner">
                  {renderStars(fb.rating)}
                </div>
              </div>
              
              <div className="bg-slate-50 p-5 rounded-2xl mt-4 relative z-10 border border-slate-100">
                <p className="text-sm text-slate-600 leading-relaxed italic">"{fb.message}"</p>
              </div>
              
              <div className="mt-5 pt-4 border-t border-slate-50 flex justify-between items-center text-[9px] text-slate-400 font-black uppercase tracking-widest relative z-10">
                <div className="flex items-center gap-3">
                  <span className="bg-slate-100 px-2 py-1 rounded-lg">ID: #{fb.id}</span>
                  <button 
                    onClick={() => toggleApproval(fb.id, fb.is_approved)}
                    className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all ${parseInt(fb.is_approved) === 1 ? 'bg-emerald-100 text-emerald-700 shadow-sm' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${parseInt(fb.is_approved) === 1 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : 'bg-slate-400'}`}></div>
                    {parseInt(fb.is_approved) === 1 ? 'Approved' : 'Pending'}
                  </button>
                </div>
                <span>{new Date(fb.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
